import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import { track, trackPageView } from '../utils/mixpanelTracking';

/**
 * Demo component to showcase improved page tracking
 * Shows actual page names instead of auto-generated events
 */
const PageTrackingDemo = () => {
  const [currentPage, setCurrentPage] = useState('');
  const [trackingHistory, setTrackingHistory] = useState([]);

  useEffect(() => {
    // Get current page info
    setCurrentPage({
      path: window.location.pathname,
      title: document.title,
      url: window.location.href
    });
  }, []);

  // Test custom event tracking
  const testCustomTracking = () => {
    track('Demo Button Clicked', {
      button_name: 'Test Custom Tracking',
      demo_page: 'Page Tracking Demo',
      timestamp: new Date().toISOString()
    });

    setTrackingHistory(prev => [...prev, {
      type: 'Custom Event',
      name: 'Demo Button Clicked',
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Test page view tracking
  const testPageViewTracking = () => {
    trackPageView('Custom Demo Page');
    
    setTrackingHistory(prev => [...prev, {
      type: 'Custom Demo Page', // Now shows actual page name as event type
      name: 'Custom Demo Page',
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Test form tracking
  const testFormTracking = () => {
    track('Form Submit', {
      form_name: 'Demo Form',
      form_data: { test: 'value' },
      demo_page: 'Page Tracking Demo'
    });

    setTrackingHistory(prev => [...prev, {
      type: 'Form Submit',
      name: 'Demo Form',
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
        🎯 Improved Page Tracking Demo
      </Typography>
      
      <Alert severity="success" sx={{ mb: 3 }}>
        <strong>✅ Problem Solved!</strong> No more "[Auto] Page..." events. 
        Now you'll see actual page names like "Dashboard", "Hosts Management", etc.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Page Information
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Path:</strong> {currentPage.path}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Title:</strong> {currentPage.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>URL:</strong> {currentPage.url}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Custom Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These events will show up in Mixpanel with meaningful names instead of auto-generated ones.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              onClick={testCustomTracking}
              color="primary"
            >
              Test Custom Event
            </Button>
            
            <Button 
              variant="contained" 
              onClick={testPageViewTracking}
              color="secondary"
            >
              Test Page View
            </Button>
            
            <Button 
              variant="contained" 
              onClick={testFormTracking}
              color="success"
            >
              Test Form Submit
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tracking History (This Session)
          </Typography>
          {trackingHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No events tracked yet. Click the buttons above to test tracking.
            </Typography>
          ) : (
            <List>
              {trackingHistory.map((event, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                     <Chip 
                             label={event.type} 
                             size="small" 
                             color={event.type.includes('Page') || event.type.includes('Demo') ? 'primary' : 'secondary'}
                           />
                          {event.name}
                        </Box>
                      }
                      secondary={event.timestamp}
                    />
                  </ListItem>
                  {index < trackingHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>What's Different Now:</strong>
        </Typography>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>✅ <strong>No more "[Auto] Page..." events</strong></li>
          <li>✅ <strong>Actual page names</strong> like "Dashboard", "Hosts Management"</li>
          <li>✅ <strong>Custom event names</strong> instead of auto-generated ones</li>
          <li>✅ <strong>Better context</strong> with page URLs, titles, and user info</li>
          <li>✅ <strong>Meaningful analytics</strong> in your Mixpanel dashboard</li>
        </ul>
      </Box>
    </Box>
  );
};

export default PageTrackingDemo;
